import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Copy, Check, Share2, Gift, TrendingUp,
  MessageCircle, Mail, Send, ExternalLink, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralSection = ({ userId }) => {
  const { t } = useTranslation();
  const [referralCode, setReferralCode] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, [userId]);

  const fetchReferralData = async () => {
    try {
      // Get referral code
      const codeRes = await axios.get(`${API}/referrals/code`, {
        headers: { 'X-User-Id': userId }
      });
      setReferralCode(codeRes.data.referral_code);

      // Get stats
      const statsRes = await axios.get(`${API}/referrals/stats`, {
        headers: { 'X-User-Id': userId }
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success(t('referrals.code_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Error copiando código');
    }
  };

  const shareVia = (platform) => {
    const message = t('referrals.share_message', { code: referralCode });
    const url = `https://lokalgrow.com?ref=${referralCode}`;
    
    const links = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message + ' ' + url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent('Te invito a Lokalgrow')}&body=${encodeURIComponent(message + '\n\n' + url)}`
    };
    
    window.open(links[platform], '_blank');
  };

  const applyCode = async () => {
    if (!inputCode.trim()) return;
    
    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/referrals/apply`,
        { referral_code: inputCode.trim() },
        { headers: { 'X-User-Id': userId } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || t('referrals.success'));
        setInputCode('');
        fetchReferralData();
      } else {
        toast.error(response.data.error || t('referrals.invalid_code'));
      }
    } catch (error) {
      const msg = error.response?.data?.detail || t('referrals.invalid_code');
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="referral-section"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center text-white">
          <Users className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">{t('referrals.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('referrals.subtitle')}</p>
        </div>
      </div>

      {/* Your Referral Code */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 rounded-xl p-4 mb-6">
        <p className="text-xs text-muted-foreground mb-2">{t('referrals.your_code')}</p>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg px-4 py-3 font-mono text-xl font-bold text-center tracking-wider">
            {referralCode}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={copyCode}
            className="h-12 w-12"
            data-testid="copy-referral-code"
          >
            {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Share Options */}
      <div className="mb-6">
        <p className="text-sm font-medium mb-3">{t('referrals.share_via')}</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => shareVia('whatsapp')}
            className="flex-1 bg-green-50 hover:bg-green-100 border-green-200 text-green-700"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => shareVia('telegram')}
            className="flex-1 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
          >
            <Send className="w-4 h-4 mr-2" />
            Telegram
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => shareVia('email')}
            className="flex-1"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.total_referrals}</p>
            <p className="text-xs text-muted-foreground">{t('referrals.stats.total')}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.completed_referrals}</p>
            <p className="text-xs text-muted-foreground">{t('referrals.stats.completed')}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending_referrals}</p>
            <p className="text-xs text-muted-foreground">{t('referrals.stats.pending')}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-pink-600">{stats.total_points_earned}</p>
            <p className="text-xs text-muted-foreground">{t('referrals.stats.points_earned')}</p>
          </div>
        </div>
      )}

      {/* Rewards Info */}
      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-5 h-5 text-amber-600" />
          <p className="font-medium text-amber-800 dark:text-amber-200">Recompensas</p>
        </div>
        <ul className="space-y-2 text-sm text-amber-700 dark:text-amber-300">
          <li>• {t('referrals.rewards.customer_customer')}</li>
          <li>• {t('referrals.rewards.customer_business')}</li>
          <li>• {t('referrals.rewards.business_business')}</li>
        </ul>
      </div>

      {/* Apply Code */}
      <div className="border-t border-border pt-4">
        <p className="text-sm font-medium mb-2">{t('referrals.enter_code')}</p>
        <div className="flex gap-2">
          <Input
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value.toUpperCase())}
            placeholder="CODIGO123"
            className="font-mono uppercase"
            maxLength={10}
            data-testid="referral-code-input"
          />
          <Button
            onClick={applyCode}
            disabled={applying || !inputCode.trim()}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="apply-referral-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('referrals.apply_code')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReferralSection;
